import React, { useMemo, useState } from 'react';
// import ConcessionDemandBalancer from './ConcessionDemandBalancer';
import ConcessionChart from './charts/ConcessionChart';
import ConcessionCountChart from './charts/ConcessionCountChart';
import ConcessionTransactionChart from './charts/ConcessionTransactionChart';
import Icon from '../../components/atoms/Icon';
import { ChartSegmented } from './charts/chartTheme';
import { Panel, PanelHeader, SortMenu } from './components/ConcessionUI';
import AdaptiveBoardLayout from './components/AdaptiveBoardLayout';
import { getQueueTimeStatus, getPeopleCountStatus, STATUS_COLORS, QUEUE_TIME_GOOD_MAX, formatQueueValue } from './concessionTheme';
import {
  seriesBySection,
  averageSeries,
  trend,
  recentWindow,
  minutesOver,
  overflowSummary,
  overflowAcrossSections,
  peakOf,
  deltaSeries,
  lastPoint,
  formatClock,
  formatDuration,
  gamePhase,
  venueNowMs,
} from './live/liveMetrics';
import DaySummary from './live/DaySummary';
import { buildConcessionDayInsights } from './live/dayInsights';
import OverviewInsights from './components/OverviewInsights';
import { Sparkline, unitFormatter, TrendBadge, KpiCard, SectionTile, UpdatedAt, OverflowAlert } from './live/LiveComponents';
import './concession.css';
import { Slot, hasSlot } from './extensions/Slot';

// People count thresholds (same as the section list and location card)
const COUNT_SECTION_LOW = 15;
const COUNT_SECTION_HIGH = 30;
const COUNT_AVG_LOW = 20;
const COUNT_AVG_HIGH = 40;

const STATUS_LABELS = {
  queue: { good: 'On target', bad: 'Over target' },
  count: { good: 'Light', warn: 'Moderate', bad: 'Busy' },
};

const ConcessionLocationLiveView = ({
  // State
  historicalData,
  historicalTransactionData,
  historicalPeopleCountData,
  stats,
  isDropdownOpen,
  currentSort,
  sectionQueueTimes,
  sectionTransactionTimes,
  sectionPeopleCounts,
  countStats,
  averagePeopleCount,
  currentGame,

  // Transaction sorting state
  currentTransactionSort,

  // Computed data
  sections,
  sectionData,
  sortOptions,

  // Transaction sorting data
  transactionSortOptions,
  sortedTransactionSections,

  // Current location data
  currentLocation,
  authCtx,
  selectedDate,
  extension,

  // Actions
  setIsDropdownOpen,
  handleSortChange,
  toggleDropdown,

  // Transaction sort actions
  handleTransactionSortChange,

  // Utility functions
  formatTransactionTime,
}) => {
  // Determine if we're in count mode or queue mode
  const isCountMode = currentLocation?.sensorDataType === "count";

  // Check if we should show delta stats (only when 2+ sections AND in queue mode)
  const showDeltaStats = sections.length >= 2 && !isCountMode;

  // Which sort drives the section board: queue/count sorts or transaction sorts
  const [sortSource, setSortSource] = useState('queue');
  const [trendTab, setTrendTab] = useState('queue');
  // Section trends: collapsed by default on live game days, open on past games
  const [trendsOpen, setTrendsOpen] = useState(false);

  // ---- Derived series ----
  const mainSeries = useMemo(
    () => seriesBySection(isCountMode ? historicalPeopleCountData : historicalData),
    [isCountMode, historicalPeopleCountData, historicalData]
  );
  const txnSeries = useMemo(() => seriesBySection(historicalTransactionData), [historicalTransactionData]);
  const avgSeries = useMemo(() => averageSeries(mainSeries), [mainSeries]);
  const avgTxnSeries = useMemo(() => averageSeries(txnSeries), [txnSeries]);
  // When data last arrived (changes on every refresh), shown with seconds in the status bar, on the venue's clock
  const receivedAt = useMemo(
    () => venueNowMs(currentLocation?.timeZone),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [historicalData, historicalPeopleCountData, historicalTransactionData]
  );
  const phase = gamePhase(currentGame, venueNowMs(currentLocation?.timeZone));
  // Game start marker for every live chart; it slides left as new data arrives
  const gameStartMarkers = phase?.start ? [{ t: phase.start, label: 'Game start' }] : [];

  // ---- Per-section current values ----
  const unit = isCountMode ? 'people' : 'mins';
  const sectionValue = (id) => (isCountMode ? sectionPeopleCounts[id] || 0 : sectionQueueTimes[id] || 0);
  // Overflow only applies to queue data: latest reading flagged means the queue is at least this long
  const sectionOverflow = (id) => (!isCountMode && !!lastPoint(mainSeries[id] || [])?.o);
  const sectionStatus = (value, overflow = false) =>
    isCountMode ? getPeopleCountStatus(value, COUNT_SECTION_LOW, COUNT_SECTION_HIGH) : getQueueTimeStatus(value, overflow);
  const statusLabels = isCountMode ? STATUS_LABELS.count : STATUS_LABELS.queue;
  const sectionThreshold = isCountMode ? COUNT_SECTION_HIGH : QUEUE_TIME_GOOD_MAX;

  const liveSections = sections.map((id) => {
    const value = sectionValue(id);
    const overflow = sectionOverflow(id);
    return { name: id, value, overflow, status: sectionStatus(value, overflow) };
  });
  const byValue = [...liveSections].sort((a, b) => b.value - a.value || b.overflow - a.overflow);
  const overflowingNow = liveSections.filter((s) => s.overflow);
  const longest = byValue[0];
  const shortest = byValue[byValue.length - 1];
  const flagged = liveSections.filter((s) => s.status === 'bad');

  // ---- Headline numbers ----
  const avgNow = isCountMode ? averagePeopleCount || 0 : stats?.currentAvgQueue || 0;
  const avgOverflow = overflowingNow.length > 0;
  const avgStatus = isCountMode
    ? getPeopleCountStatus(avgNow, COUNT_AVG_LOW, COUNT_AVG_HIGH)
    : getQueueTimeStatus(avgNow, avgOverflow);
  // Overflow across the day: time any section was overflowing, from the raw readings
  const overflowToday = overflowAcrossSections(mainSeries);
  const sectionsWithOverflow = sections.filter((id) => overflowSummary(mainSeries[id] || []).minutes > 0);
  // Did the day's peak average minute include an overflowing section?
  const dayPeakPoint = peakOf(avgSeries);
  const dayPeakOverflow = !isCountMode && !!dayPeakPoint?.o && Math.round(dayPeakPoint.y) === (stats?.busiestTimeAvgQueue || 0);
  const avgThreshold = isCountMode ? COUNT_AVG_HIGH : QUEUE_TIME_GOOD_MAX;
  const overToday = minutesOver(avgSeries, avgThreshold, !isCountMode);
  const totalInLine = liveSections.reduce((sum, s) => sum + s.value, 0);

  // "What stands out": findings from today so far
  const insights = useMemo(
    () => buildConcessionDayInsights({ mainSeries, txnSeries, isCountMode, gameStart: phase?.start ?? null, isLive: true, countSectionBusyAt: COUNT_SECTION_HIGH, countAvgBusyAt: COUNT_AVG_HIGH, formatTransactionTime }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mainSeries, txnSeries, isCountMode, phase?.start]
  );

  // ---- Section board order (uses the controller's sorts) ----
  const queueOrder = isCountMode
    ? [...sections]
        .map((name, index) => ({ id: index + 1, name, count: sectionPeopleCounts[name] || 0 }))
        .sort((a, b) => {
          switch (currentSort.value) {
            case 'id-desc':
              return b.id - a.id;
            case 'current-queue-asc': // Reuse for count sorting
              return a.count - b.count;
            case 'current-queue-desc': // Reuse for count sorting
              return b.count - a.count;
            default:
              return a.id - b.id;
          }
        })
        .map((s) => s.name)
    : sectionData.map((s) => s.name);
  const boardOrder = !isCountMode && sortSource === 'transaction' ? sortedTransactionSections : queueOrder;

  const boardSortOptions = isCountMode ? sortOptions : [...sortOptions, ...transactionSortOptions];
  const boardCurrentSort = !isCountMode && sortSource === 'transaction' ? currentTransactionSort : currentSort;
  const handleBoardSort = (option) => {
    if (transactionSortOptions.some((o) => o.value === option.value)) {
      setSortSource('transaction');
      handleTransactionSortChange(option);
      setIsDropdownOpen(false);
    } else {
      setSortSource('queue');
      handleSortChange(option);
    }
  };

  const sectionStatsFor = (id) => (isCountMode ? countStats?.sectionStats?.[id] : stats?.sectionStats?.[id]);

  const tileDetails = (id) => {
    const s = sectionStatsFor(id);
    const over = minutesOver(mainSeries[id] || [], sectionThreshold, !isCountMode);
    if (isCountMode) {
      return [
        { label: 'Peak today', value: `${s?.maxCount ?? 0} people`, at: s?.maxCountTime },
        { label: 'Average today', value: `${s?.avgCount ?? 0} people` },
        { label: 'Time busy', value: formatDuration(over.minutes) },
      ];
    }
    const peak = peakOf(mainSeries[id] || []);
    const overflow = overflowSummary(mainSeries[id] || []);
    // Pairs: queue | queue, overflow | overflow, transaction | transaction
    return [
      { label: 'Peak today', value: `${formatQueueValue(s?.maxQueue ?? 0, peak?.o && peak?.y === s?.maxQueue)} min`, at: s?.maxQueueTime, alert: !!(peak?.o && peak?.y === s?.maxQueue) },
      { label: 'Over target today', value: formatDuration(over.minutes) },
      { label: 'Overflow duration', value: overflow.minutes > 0 ? formatDuration(overflow.minutes) : 'None', alert: overflow.minutes > 0 },
      { label: 'Times in overflow', value: `${overflow.episodes}`, alert: overflow.episodes > 0 },
      { label: 'Transaction now', value: formatTransactionTime(sectionTransactionTimes[id] || 0) },
      { label: 'Avg transaction', value: formatTransactionTime(s?.avgTransaction || 0) },
    ];
  };

  // ---- Trends tabs ----
  const trendOptions = isCountMode
    ? []
    : [
        { value: 'queue', label: 'Queue times' },
        { value: 'transaction', label: 'Transactions' },
      ];

  const hasChartData = historicalData.length > 0 || historicalPeopleCountData.length > 0;

  return (
    <div className="cx-live">
      {/* Status bar: game phase, schedule, freshness */}
      <div className="cx-live-status">
        <div className="cx-live-status-left">
          {phase && <span className="cx-phase">{phase.label}</span>}
          {phase && <span className="cx-live-status-detail">{phase.detail}</span>}
          {phase?.gates && <span className="cx-live-status-meta">Gates {formatClock(phase.gates)}</span>}
          {phase?.start && <span className="cx-live-status-meta">Game start {formatClock(phase.start)}</span>}
        </div>
        <div className="cx-live-status-right">
          <UpdatedAt t={receivedAt} withSeconds />
          <span className="cx-live-status-meta">Auto-refreshes every 60s</span>
        </div>
      </div>

      {/* Overflow alert: live */}
      {overflowingNow.length > 0 && (
        <OverflowAlert
          title={`Overflow now at ${overflowingNow.map((s) => `Section ${s.name}`).join(', ')}`}
        >
          {overflowingNow.map((s) => `Section ${s.name}: ${s.value}+ min`).join(' · ')}
        </OverflowAlert>
      )}

      {/* Headline KPIs */}
      <div className={`cx-kpi-grid${hasSlot(extension, 'gameDay.kpis') ? ' cx-kpi-grid-auto' : ''}`}>
        <KpiCard
          label={isCountMode ? 'Average count now' : 'Average queue now'}
          info={isCountMode
            ? 'Average people count across active sections right now, from each section\'s latest reading.'
            : `Average queue time across all sections right now, from each section's latest reading. Under ${QUEUE_TIME_GOOD_MAX} min is on target; ${QUEUE_TIME_GOOD_MAX} min or more is over.`}
          accent={STATUS_COLORS[avgStatus]}
          value={<>{avgNow}{avgOverflow && <span className="cx-plus">+</span>}<span className="cx-unit">{unit}</span></>}
        >
          <TrendBadge delta={trend(avgSeries)} unit={isCountMode ? 'people' : 'min'} />
          <Sparkline
            points={recentWindow(avgSeries, 60)}
            color="var(--q-text-subtle)"
            valueColor={(v) => STATUS_COLORS[isCountMode ? getPeopleCountStatus(v, COUNT_AVG_LOW, COUNT_AVG_HIGH) : getQueueTimeStatus(v)]}
            target={isCountMode ? undefined : QUEUE_TIME_GOOD_MAX}
            minMax={isCountMode ? COUNT_AVG_HIGH : QUEUE_TIME_GOOD_MAX * 1.5}
            formatValue={unitFormatter(isCountMode ? 'people' : 'min')}
            markers={gameStartMarkers}
          />
          <div className="cx-kpi-foot">Last 60 minutes</div>
        </KpiCard>

        <KpiCard
          label={isCountMode ? 'Busiest section' : 'Longest queue'}
          accent={longest ? STATUS_COLORS[longest.status] : undefined}
          value={<>{longest?.value ?? 0}{!!longest?.overflow && <span className="cx-plus">+</span>}<span className="cx-unit">{unit}</span></>}
        >
          <div className="cx-kpi-sub">
            {longest ? `Section ${longest.name}` : 'No sections'}
            {!!longest?.overflow && <span className="cx-inline-overflow">Overflow</span>}
          </div>
          {showDeltaStats && shortest && longest && longest !== shortest && (
            <div className="cx-kpi-foot">
              Shortest: Section {shortest.name} at {formatQueueValue(shortest.value, shortest.overflow)} min
            </div>
          )}
        </KpiCard>

        <KpiCard
          label={isCountMode ? 'Busy sections' : 'Sections over target'}
          info={isCountMode
            ? `Sections with more than ${COUNT_SECTION_HIGH} people right now.`
            : `Sections with a queue over ${QUEUE_TIME_GOOD_MAX} min right now.`}
          accent={flagged.length > 0 ? STATUS_COLORS.bad : STATUS_COLORS.good}
          value={<>{flagged.length}<span className="cx-unit">of {liveSections.length}</span></>}
        >
          {/* Every section gets an icon: check = on target, ! = over target, triangle = overflow */}
          <div className="cx-status-chips">
            {liveSections.map((s) => {
              const icon = s.overflow
                ? 'exclamation-triangle-fill'
                : s.status === 'good'
                  ? 'check-circle-fill'
                  : 'exclamation-circle-fill';
              const state = s.overflow ? 'overflow' : s.status;
              return (
                <span
                  key={s.name}
                  className={`cx-status-chip cx-status-chip-${state}`}
                  title={`Section ${s.name}: ${formatQueueValue(s.value, s.overflow)} ${unit}${s.overflow ? ' (overflow)' : ''}`}
                >
                  <Icon name={icon} className="cx-status-chip-icon" />
                  <span className="cx-status-chip-name">{s.name}</span>
                </span>
              );
            })}
          </div>
          <div className="cx-kpi-foot">
            {flagged.length === 0 ? 'All sections on target' : `Needs attention: ${flagged.map((s) => s.name).join(', ')}`}
            {overflowingNow.length > 0 && ` · ${overflowingNow.length} overflowing`}
          </div>
        </KpiCard>

        {isCountMode ? (
          <KpiCard
            label="Total in line"
            info="Sum of people counted across all sections right now."
            value={<>{totalInLine}<span className="cx-unit">people</span></>}
          >
            <div className="cx-kpi-foot">Across {liveSections.length} sections</div>
          </KpiCard>
        ) : (
          <KpiCard
            label="Average transaction now"
            info="Average register transaction time across sections right now. Lower means faster service."
            value={formatTransactionTime(stats?.currentAvgTransaction || 0)}
          >
            <TrendBadge delta={trend(avgTxnSeries)} format={(v) => formatTransactionTime(v)} />
            <Sparkline points={recentWindow(avgTxnSeries, 60)} color="var(--q-brand)" height={36} formatValue={(v) => formatTransactionTime(v)} markers={gameStartMarkers} />
            <div className="cx-kpi-foot">Last 60 minutes</div>
          </KpiCard>
        )}
        {/* Venue-specific cards (extensions/registry.js) */}
        <Slot
          name="gameDay.kpis"
          extension={extension}
          authCtx={authCtx}
          currentLocation={currentLocation}
          currentGame={currentGame}
          selectedDate={selectedDate}
          isLive={true}
          refreshKey={historicalData}
        />
      </div>

      {insights.length > 0 && (
        <div className="mb-4">
          <Panel>
            <PanelHeader
              title="What stands out"
              subtitle="Findings from today so far, most important first. They update with the live data every minute."
              actions={<UpdatedAt t={receivedAt} />}
            />
            <OverviewInsights insights={insights} />
          </Panel>
        </div>
      )}

      {/* Section board + side panels: adaptive so nothing stretches into dead space */}
      <AdaptiveBoardLayout
        pinFirst
        board={
          <Panel>
            <PanelHeader
              title="Sections"
              subtitle={isCountMode
                ? 'Live people count by section with the last 60 minutes.'
                : 'Live queue time by section with the last 60 minutes. Dashed line is the target.'}
              actions={
                <SortMenu
                  isOpen={isDropdownOpen}
                  onToggle={toggleDropdown}
                  options={boardSortOptions}
                  current={boardCurrentSort}
                  onChange={handleBoardSort}
                  minWidth={320}
                  formatLabel={(label) => label.replace('Queue Time', isCountMode ? 'Count' : 'Queue Time')}
                />
              }
            />
            <div className="cx-tile-grid">
              {boardOrder.map((id) => {
                const value = sectionValue(id);
                const overflow = sectionOverflow(id);
                const status = sectionStatus(value, overflow);
                return (
                  <SectionTile
                    key={id}
                    name={`Section ${id}`}
                    value={value}
                    overflow={overflow}
                    unit={unit}
                    status={status}
                    statusFor={(v) => sectionStatus(v)}
                    statusLabel={statusLabels[status]}
                    trendDelta={trend(mainSeries[id] || [])}
                    spark={recentWindow(mainSeries[id] || [], 60)}
                    target={isCountMode ? undefined : QUEUE_TIME_GOOD_MAX}
                    sparkMin={isCountMode ? COUNT_SECTION_HIGH : QUEUE_TIME_GOOD_MAX * 1.5}
                    details={tileDetails(id)}
                    markers={gameStartMarkers}
                  />
                );
              })}
            </div>
          </Panel>
        }
        panels={[
          { key: 'today', node: (
            <Panel>
              <PanelHeader title="Today so far" info={"Statistics of all sections for the entire day."+" Averages use every reading, so they can sit a little below the charts, which show the highest reading in each interval."} />
              <DaySummary
                queue={isCountMode
                  ? {
                      title: 'People count',
                      avg: countStats?.dayAverageCount || 0,
                      peak: countStats?.busiestTimeAvgCount || 0,
                      peakAt: countStats?.busiestTime,
                      avgStatus: getPeopleCountStatus(countStats?.dayAverageCount || 0, COUNT_AVG_LOW, COUNT_AVG_HIGH),
                      statusFor: (v) => getPeopleCountStatus(v, COUNT_AVG_LOW, COUNT_AVG_HIGH),
                      peakStatus: getPeopleCountStatus(countStats?.busiestTimeAvgCount || 0, COUNT_AVG_LOW, COUNT_AVG_HIGH),
                      unit: 'people',
                    }
                  : {
                      title: 'Queue',
                      avg: stats?.dayAverageQueue || 0,
                      peak: stats?.busiestTimeAvgQueue || 0,
                      peakAt: stats?.busiestTime,
                      peakOverflow: dayPeakOverflow,
                      avgStatus: getQueueTimeStatus(stats?.dayAverageQueue || 0),
                      statusFor: (v) => getQueueTimeStatus(v),
                      peakStatus: getQueueTimeStatus(stats?.busiestTimeAvgQueue || 0, dayPeakOverflow),
                      target: QUEUE_TIME_GOOD_MAX,
                      unit: 'min',
                    }}
                time={{
                  total: overToday.total,
                  over: overToday.minutes,
                  overflow: isCountMode ? 0 : overflowToday.minutes,
                  overflowEpisodes: overflowToday.episodes,
                  overflowSections: sectionsWithOverflow,
                  overLabel: isCountMode ? 'Time busy' : 'Time over target',
                }}
                delta={showDeltaStats ? {
                  avg: stats?.dayAverageDelta || 0,
                  peak: stats?.historicalMaxDelta || 0,
                  peakAt: stats?.historicalMaxDeltaTime,
                } : null}
                transaction={isCountMode ? null : {
                  avg: stats?.dayAverageTransaction || 0,
                  peak: stats?.peakTransactionAvg || 0,
                  peakAt: stats?.peakTransactionTime,
                  format: formatTransactionTime,
                }}
                series={{ main: avgSeries, txn: avgTxnSeries, delta: deltaSeries(mainSeries) }}
                overThreshold={avgThreshold}
                overAtOrAbove={!isCountMode}
                markers={gameStartMarkers}
              />
            </Panel>
          ) },
        ]}
      />

      {/* Trends */}
      {hasChartData && (
        <div className={`cx-trends${trendsOpen ? ' open' : ''}`}>
          <div className="cx-trends-head">
            <button
              type="button"
              className="cx-trends-toggle"
              onClick={() => setTrendsOpen((o) => !o)}
              aria-expanded={trendsOpen}
            >
              <Icon name={trendsOpen ? 'chevron-down' : 'chevron-right'} className="cx-trends-chevron" />
              <span>
                <span className="cx-panel-title">Section trends</span>
                <span className="cx-panel-subtitle">Each section over time. Zoom, pan and compare sections side by side.</span>
              </span>
            </button>
            {trendsOpen && trendOptions.length > 1 && (
              <ChartSegmented options={trendOptions} value={trendTab} onChange={setTrendTab} />
            )}
          </div>

          {trendsOpen && (isCountMode ? (
            historicalPeopleCountData.length > 0 && (
              <ConcessionCountChart
                historicalPeopleCountData={historicalPeopleCountData}
                sections={sections}
                currentGame={currentGame}
              />
            )
          ) : (
            <>
              {trendTab === 'queue' && historicalData.length > 0 && (
                <ConcessionChart
                  name={currentLocation.name}
                  sections={sections}
                  historicalData={historicalData}
                  currentGame={currentGame}
                />
              )}
              {trendTab === 'transaction' && historicalTransactionData.length > 0 && (
                <ConcessionTransactionChart
                  historicalTransactionData={historicalTransactionData}
                  sections={sections}
                  formatTransactionTime={formatTransactionTime}
                  currentGame={currentGame}
                />
              )}
            </>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConcessionLocationLiveView;
